import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { SessionStorageService, SessionStorage } from 'ngx-webstorage';

import { AwardsService } from 'services/awards/awards.service';
import { SettingsService } from 'services/settings/settings.service';
import { ApplicationService, SchoolInfo } from '../application/application.service';
import { takeUntil } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class UserService implements OnDestroy {
	private _subrelease: Subject<void> = new Subject<void>();
    @SessionStorage() private _User: UserInfo;
    UserSubject: BehaviorSubject<UserInfo> = new BehaviorSubject<UserInfo>(null);

    get User() {
        return this._User;
    }

    set User(_ui: UserInfo) {
        this._User = _ui;
        this.UserSubject.next(_ui);
    }

    constructor(
        private awardsService: AwardsService,
        private storage: SessionStorageService,
        private router: Router,
        private sessionService: SessionStorageService,
		private settingsService: SettingsService,
		private appService: ApplicationService
    ) {
        this.UserSubject.next(this._User);
	}

	ngOnDestroy() {
		this._subrelease.next(); this._subrelease.complete();
	}

    // #region Clear User Service Call

    ClearUser() {
        this.User = null;
        this.sessionService.clear('_User');

        // Clear out all session specific settings.
        this.storage.clear('LastSearch');
		this.storage.clear('LastSearchParams');
		this.settingsService.SetCurrentSchool(null);
    }

    // #endregion

    // #region Set Designee Edit Rights Service Call

    SetDesigneeEditRights(rights: DesigneeEditRights) {
        const newUser: UserInfo = <UserInfo>JSON.parse(JSON.stringify(this.User));

        // newUser.CanEditSchool = (rights != null ? rights.CanEditSchool : false);
        // newUser.CanEditTeacher = (rights != null ? rights.CanEditTeacher : false);
        // newUser.CanUploadVideos = (rights != null ? rights.CanUploadVideos : false);

        this.User = newUser;
    }

    // #endregion

    // #region Set User Service Call

    SetUser(user: UserInfo) {
        // const currole = this.User && this.User.CurrentRole;
        // user.CurrentRole = currole;

        this.User = user;
    }

    // #endregion

    // #region Set User Current Role Service Call

    SetUserCurrentRole(role: string, location?: string) {
        // To trigger proper session storage, we need to create a new object reference, rather than just updating our existing User object.
        const newUser: UserInfo = <UserInfo>JSON.parse(JSON.stringify(this.User));

        // Update the current role field.
		newUser.CurrentRole = role;
		newUser.InSchoolGroup = false;

		this.settingsService.CurrentSchoolYearSubject.pipe(takeUntil(this._subrelease)).subscribe(year => {
			this.appService.GetAllSchools(year).pipe(takeUntil(this._subrelease)).subscribe(schools => {
				this.appService.GetAllSchoolGroups(this.User.AwardID, year).pipe(takeUntil(this._subrelease)).subscribe(groups => {
					//for switching roles
					if (location) {
						newUser.SchoolNumber = location;

						//if school is in school group, set LeadLocationNumber school as currentSchool
						for (let g of groups) {
							for (let s of g.Schools) {
								if (s.LocationNumber === location) {
									newUser.SchoolNumber = g.LeadLocationNumber;
									newUser.InSchoolGroup = true;

									let schoolNumberList = [];

									for (let i of g.Schools) {
										schoolNumberList.push(i.LocationNumber);
									}

									newUser.SchoolNumberList = schoolNumberList.join(', ');
								}
							}
						}
					}
					//when log in
					else {
						let foundRole = this.User.Roles.find(r => r.RoleName === role);
						if (foundRole && foundRole.WorkLocation) {
							newUser.SchoolNumber = foundRole.WorkLocation;
						}

						//if school is in school group, set LeadLocationNumber school as currentSchool
						for (let g of groups) {
							for (let s of g.Schools) {
								if (s.LocationNumber === foundRole.WorkLocation) {
									newUser.SchoolNumber = g.LeadLocationNumber;
									newUser.InSchoolGroup = true;

									let schoolNumberList = [];

									for (let i of g.Schools) {
										schoolNumberList.push(i.LocationNumber);
									}

									newUser.SchoolNumberList = schoolNumberList.join(', ');
								}
							}
						}
					}

					let school = schools.find(f => f.SchoolNumber === newUser.SchoolNumber);
					this.settingsService.SetCurrentSchool(school);

					// Now, persist it to session storage and then broadcast it for listeners.
					this.User = newUser;

					// Go to the home screen for this role.
					let homeroute = `${this.awardsService.CurrentAwards.URLPath}/home`;

					if (role === 'Judge') {
						homeroute = `${this.awardsService.CurrentAwards.URLPath}/judgenominees`
					}

					this.router.navigate([homeroute]);
				});
			});
		});
    }

    // #endregion

    // #region Update AuthToken Service Call

    UpdateAuthToken(AuthToken: any) {
        // If the authtoken has changed, update the user record.
        if (AuthToken !== this.User.AuthToken) {
            // To trigger proper session storage, we need to create a new object reference, rather than just updating our
            // existing User object.
            const newUser: UserInfo = <UserInfo>JSON.parse(JSON.stringify(this.User));

            // Update the AuthToken field
            newUser.AuthToken = AuthToken;

            // Now, persist it to session storage and then broadcast it for listeners
            this.User = newUser;
        }
    }

    // #endregion
}

// #region Custom Classes

// #region Designee Edit Rights Class

export class DesigneeEditRights {
    CanEditSchool: boolean;
    CanEditTeacher: boolean;
    CanUploadVideos: boolean;
}

// #endregion

// #region User Info Class

export class UserInfo {
    AccountName: string;
    AwardID: number;
    AuthToken: string;
    CurrentRole: string;
    CurrentSchool: SchoolInfo;
    FirstName: string;
    HasUserRole: boolean;
    ImpersonatorPositionNo: string;
    ImpersonatorSAPID: string;
    ImpersonatorUserID?: number;
    ImpersonationAvailable: boolean;
    IsImpersonation: boolean;
    IsMultiRole: boolean;
    LastName: string;
    LocalJobTitle: string;
    SAPID: string;
    UserID: number;
    Roles: UserRole[];
    SchoolName: string;
	SchoolNumber: string;
	InSchoolGroup: boolean;
	SchoolNumberList: string;
    TrainingMode?: boolean;
}

// #endregion

// #region User Role Class

export class UserRole {
    DistrictNumber: number;
    Rights: string[];
    RoleName: string;
    RoleNumber: number;
    SortOrder: number;
	WorkLocation: string;
	WorkLocationList: string;
	InSchoolGroup: boolean;
    NomineeType: number;
}

// #endregion

// #endregion
